import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../LocalizedLink/LocalizedLink";
import { localizedPath } from "../../utils/lang";
import { useBlog } from "../../config/BlogProvider";
import { useLanguage } from "../../context/LanguageContext";
import { ikUrl } from "../../utils/imagekit";
import {
  getTranslation,
  deriveCategories,
  deriveTags,
  formatDate,
} from "../../utils/article";

const ClickHandler = () => {
  window.scrollTo(10, 0);
};

const BlogSidebar = (props) => {
  const { articles } = useBlog();
  const { lang } = useLanguage();
  const { t } = useTranslation("blog");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const q = searchParams.get("q") || "";
  const activeCat = searchParams.get("cat") || "";

  const categories = deriveCategories(articles, lang);
  const tags = deriveTags(articles);
  const recent = (articles || []).slice(0, 4);

  // Update a filter param. On the article-details page, redirect to /blog so the
  // filtered list is actually visible.
  const setParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    const blogPath = localizedPath(lang, "/blog");
    const onList = location.pathname === blogPath;
    navigate(`${blogPath}?${params.toString()}`, { replace: onList });
  };

  return (
    <div className={`col col-lg-4 col-12 ${props.blLeft || ""}`}>
      <div className="blog-sidebar">
        <div className="widget search-widget">
          <h3>{t("searchHere")}</h3>
          <form onSubmit={(e) => e.preventDefault()}>
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={t("searchPlaceholder")}
                value={q}
                onChange={(e) => setParam("q", e.target.value)}
              />
              <button type="submit">
                <i className="ti-search"></i>
              </button>
            </div>
          </form>
        </div>

        {categories.length > 0 && (
          <div className="widget category-widget">
            <h3>{t("categories")}</h3>
            <ul>
              {categories.map((c) => (
                <li key={c.slug}>
                  <button
                    type="button"
                    className={
                      activeCat === c.slug
                        ? "cat-link active"
                        : "cat-link"
                    }
                    onClick={() => setParam("cat", c.slug)}
                  >
                    {t(`cat.${c.slug}`, { defaultValue: c.slug })}
                    <span>{c.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {recent.length > 0 && (
          <div className="widget recent-post-widget">
            <h3>{t("recentPosts")}</h3>
            <div className="posts">
              {recent.map((article) => {
                const picked = getTranslation(article, lang);
                if (!picked) return null;
                const { tr } = picked;
                return (
                  <div className="post" key={article.id}>
                    {article.coverImage && (
                      <div className="img-holder">
                        <img
                          src={ikUrl(
                            article.coverImage.url || article.coverImage.path,
                            "w-120,q-70,f-auto"
                          )}
                          alt={article.coverImage.alt || tr.title}
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="details">
                      <h4>
                        <LocalizedLink
                          onClick={ClickHandler}
                          to={`/blog/${tr.slug}`}
                        >
                          {tr.title}
                        </LocalizedLink>
                      </h4>
                      <span className="date">
                        {formatDate(article.publishedAt, lang)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tags.length > 0 && (
          <div className="widget tag-widget">
            <h3>{t("tags")}</h3>
            <ul>
              {tags.map((tag) => (
                <li key={tag}>
                  <button
                    type="button"
                    className="tag-link"
                    onClick={() => setParam("q", tag)}
                  >
                    {t(`tag.${tag}`, { defaultValue: tag })}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogSidebar;
